import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query'
import { listarAncoras, criarAncora } from './api'

function useDebounce<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return debounced
}

export default function EquivalenciaList() {
  const navigate = useNavigate()
  const [busca, setBusca] = useState('')
  const [apenasComMembros, setApenasComMembros] = useState(false)
  const [criando, setCriando] = useState(false)
  const [novoNome, setNovoNome] = useState('')
  const [novaUnidade, setNovaUnidade] = useState('')
  const q = useDebounce(busca.trim(), 300)

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ['ancoras', q, apenasComMembros],
    queryFn: () => listarAncoras(q || undefined, apenasComMembros),
    placeholderData: keepPreviousData,
  })

  const mCriar = useMutation({
    mutationFn: () => criarAncora(novoNome.trim(), novaUnidade.trim() || undefined),
    onSuccess: (r) => {
      setCriando(false)
      setNovoNome('')
      setNovaUnidade('')
      navigate(`/equivalencia/${r.id}`)
    },
  })

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Equivalência de produtos</h1>
          <p className="text-sm text-slate-500">
            Agrupe medicamentos equivalentes (mesmo princípio ativo e dosagem) sob uma âncora
            genérica. O estoque é somado e a compra sai no código da âncora — sem precisar criar item no ERP.
          </p>
        </div>
        <button
          onClick={() => setCriando((v) => !v)}
          className="shrink-0 text-sm px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
        >
          + Criar âncora
        </button>
      </div>

      {criando && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <label className="block text-xs text-slate-600 mb-1">Nome da âncora (princípio ativo + dosagem)</label>
            <input
              autoFocus
              value={novoNome}
              onChange={(e) => setNovoNome(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && novoNome.trim()) mCriar.mutate() }}
              placeholder="Ex.: DIPIRONA 500MG/ML INJETÁVEL"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="w-28">
            <label className="block text-xs text-slate-600 mb-1">Unidade</label>
            <input
              value={novaUnidade}
              onChange={(e) => setNovaUnidade(e.target.value)}
              placeholder="UN"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => mCriar.mutate()}
            disabled={!novoNome.trim() || mCriar.isPending}
            className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
          >
            {mCriar.isPending ? 'Criando…' : 'Criar'}
          </button>
          <button onClick={() => setCriando(false)} className="text-sm px-3 py-2 text-slate-500 hover:text-slate-700">
            Cancelar
          </button>
          {mCriar.error && (
            <p className="w-full text-xs text-red-600">
              {mCriar.error instanceof Error ? mCriar.error.message : 'Erro ao criar'}
            </p>
          )}
        </div>
      )}

      <div className="relative mb-3">
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar âncora por nome ou código de compra…"
          className="w-full px-4 py-2.5 pl-10 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {isFetching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      <label className="flex items-center gap-2 text-xs text-slate-600 mb-4 select-none cursor-pointer">
        <input
          type="checkbox"
          checked={apenasComMembros}
          onChange={(e) => setApenasComMembros(e.target.checked)}
        />
        Mostrar apenas âncoras que já têm membros vinculados
      </label>

      {error && (
        <p className="p-8 text-red-600">
          Erro: {error instanceof Error ? error.message : String(error)}
          <button onClick={() => refetch()} className="ml-2 underline">tentar de novo</button>
        </p>
      )}
      {isLoading && <p className="p-8 text-slate-500">Carregando…</p>}

      {data && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Cód. compra</th>
                <th className="text-left px-4 py-3">Âncora genérica</th>
                <th className="text-left px-4 py-3">Origem</th>
                <th className="text-center px-4 py-3">Membros</th>
                <th className="text-right px-4 py-3">Estoque somado</th>
              </tr>
            </thead>
            <tbody>
              {data.map((a) => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">
                    <Link to={`/equivalencia/${a.id}`} className="text-blue-700 hover:underline">
                      {a.codigo_compra}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-slate-800">
                    <Link to={`/equivalencia/${a.id}`} className="hover:text-blue-700">
                      {a.nome}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-400">
                    {a.origem_erp ? `ERP ${a.origem_erp}` : 'nativa'}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className={a.qtd_membros > 0 ? 'text-green-600 font-medium' : 'text-slate-300'}>
                      {a.qtd_membros}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {Number(a.estoque_total).toLocaleString('pt-BR')}
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Nenhuma âncora encontrada</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
